import { VideoEditorLogger } from '../../utility/VideoEditorLogger';
import { AddMediaElementCommand } from './AddMediaElementCommand';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';
import type { TimelineMode } from './TimelineMode';

/**
 * Command for adding composition elements to the timeline with specialized composition handling.
 * Extends AddMediaElementCommand to inherit collision detection and track management,
 * while adding composition-specific initialization and validation.
 */
export class AddCompositionElementCommand extends AddMediaElementCommand {
  constructor(
    editor: VideoEditor,
    element: Element,
    targetTrackId?: string,
    mode?: TimelineMode,
  ) {
    super(editor, element, targetTrackId, mode ?? editor.getTimelineMode());
  }

  execute(): boolean {
    VideoEditorLogger.logCommand(this, 'start', {
      elementId: this.element.id,
      elementType: this.element.type,
      targetTrackId: this.targetTrackId,
      mode: this.mode,
    });

    // Validate composition element properties
    if (!this.validateCompositionElement()) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Composition element validation failed',
      });
      return false;
    }

    // Call parent execute which handles timeline placement and collision detection
    const success = super.execute();

    if (success) {
      VideoEditorLogger.logCommand(this, 'complete', {
        elementId: this.element.id,
        finalTrackId: this.finalTrackId,
      });
    }

    return success;
  }

  getDescription(): string {
    const trackInfo = this.trackCreated
      ? `new composition track`
      : `track ${this.finalTrackId || this.targetTrackId || 'auto'}`;
    return `Add composition element to ${trackInfo} (${this.mode} mode)`;
  }

  /**
   * Validates that the composition element has required properties
   */
  private validateCompositionElement(): boolean {
    if (this.element.type !== 'composition') {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Element is not a composition type',
        actualType: this.element.type,
      });
      return false;
    }

    // Ensure element has a valid duration
    if (this.element.duration === null || this.element.durationSeconds <= 0) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Composition element must have a valid duration',
        duration: this.element.duration,
      });
      return false;
    }

    return true;
  }
}
